import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useShippingCalculator } from "../hooks/useShippingCalculator";
import { usePosConfig } from "../hooks/usePosConfig";

interface MapProps {
  setAddress: (address: string) => void;
  setShipping: (shipping: number) => void;
  setLocation: (location: any) => void;
  orderTotal: number;
  address: string;
  defaultPoint: IMarker;
  /** Precio BASE (sin descuento de envío gratis) + distancia de la última
   *  cotización, para que quien use <Map> pueda cachearlos tal cual sin
   *  tener que recalcular por su cuenta (evita resultados inconsistentes). */
  onQuote?: (rawPrice: number, distanceMeters: number, durationSeconds: number) => void;
}

const containerStyle = {
  width: "100%",
  height: "500px",
};

interface IMarker {
  lat: number;
  lng: number;
}
export const Map: React.FC<MapProps> = ({
  setAddress,
  setLocation,
  orderTotal,
  setShipping,
  address,
  defaultPoint: defaultCenter,
  onQuote,
}) => {
  const { calculateShipping, isLoaded } = useShippingCalculator();
  const { config, loading: configLoading } = usePosConfig();
  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(16);
  const [map, setMap] = React.useState(null);
  const [shippingPrice, setShippingPrice] = useState(0);
  const [distance, setDistance] = useState(0);
  const [time, setTime] = useState("");
  const [markers, setMarkers] = useState<IMarker[]>([defaultCenter]);
  // Último punto cotizado; si config/pos aún no había cargado cuando se pidió,
  // se recalcula solo en cuanto la config real esté lista (ver efecto abajo).
  const [lastPoint, setLastPoint] = useState<IMarker | null>(null);
  // <Marker position={center} />


  const onLoad = React.useCallback(function callback(map: any) {
    // const bounds = new window.google.maps.LatLngBounds(center);
    // map.fitBounds(bounds);
    // setMap(map);
  }, []);
  const getDistance = (des: IMarker) => {
    setLastPoint(des);
    if (configLoading) return;
    calculateShipping(defaultCenter, des, config, orderTotal).then((result) => {
      // No pisar el input con `result.destinationAddress`: es un geocodificado
      // inverso de la API de Distance Matrix que puede "redondear" al número
      // de puerta más cercano que tiene catalogado (ej. 700 en vez de 704),
      // distinto de la dirección que el cajero ya tecleó/seleccionó.
      setShippingPrice(result.price);
      setShipping(result.price);
      setDistance(result.distanceMeters);
      setTime(result.durationText);
      onQuote?.(result.rawPrice, result.distanceMeters, result.durationSeconds);
    });
  };
  // Si el cajero cotizó antes de que cargara la config real (basePrice/km/min
  // por default), recalcula automáticamente en cuanto esté lista.
  useEffect(() => {
    if (!configLoading && lastPoint) {
      getDistance(lastPoint);
    }
  }, [configLoading]);
  // El listener del autocomplete (abajo) se registra una sola vez, cuando
  // isLoaded pasa a true — si eso ocurre antes de que cargue la config real,
  // quedaría con una versión "congelada" de getDistance que siempre ve
  // configLoading=true y nunca calcula nada. Este ref siempre apunta a la
  // versión más reciente, evitando ese closure obsoleto.
  const getDistanceRef = useRef(getDistance);
  useEffect(() => {
    getDistanceRef.current = getDistance;
  });
  const onUnmount = React.useCallback(function callback(map: any) {
    setMap(null);
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const setMarker = (place: google.maps.places.PlaceResult) => {
    if (place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      console.log({ place });
      const result = { lat, lng };
      setMarkers([result]);
      setCenter(result);
    }
  };
  const setMarkerByMark = (place: IMarker) => {
    // if(place.geometry && place.geometry.location){
    const lat = place.lat;
    const lng = place.lng;
    const result = { lat, lng };
    setMarkers([result]);
    setCenter(result);
    setZoom(15);
    // }
  };
  const geocode = (request: google.maps.GeocoderRequest): void => {
    // clear();

    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode(request)
      .then((result) => {
        const { results } = result;
        const resultPoint = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        setMarkers([resultPoint]);
        setCenter(resultPoint);
        setZoom(16);
        getDistance(resultPoint);
        // Al arrastrar el marcador no hay un texto tecleado/seleccionado, así
        // que sí usamos el geocodificado inverso aquí (es la única fuente de
        // dirección disponible para este flujo).
        setAddress(results[0].formatted_address);
        setLocation({
          ...resultPoint,
          results,
        });

      })
      .catch((e) => {
        console.log("Geocode was not successful for the following reason: " + e);
      });
  };

  const geocode2 = (request: google.maps.GeocoderRequest): void => {
    // clear();

    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode(request)
      .then((result) => {
        const { results } = result;
        const resultPoint = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        // setMarkers([resultPoint]);
        setCenter(resultPoint);
        setZoom(16);
        getDistance(resultPoint);
        setLocation({
          ...resultPoint,
          results,
        });
      })
      .catch((e) => {
        console.log("Geocode was not successful for the following reason: " + e);
      });
  };
  const onDragMarker = (coord: any) => {
    const { latLng } = coord;
    const lat = latLng.lat();
    const lng = latLng.lng();
    setMarkers([{ lat, lng }]);
    geocode({
      location: { lat, lng },
    })
    console.log(lat);

};
  useEffect(() => {
    if (!isLoaded) return;

    const options = {
      componentRestrictions: { country: "mx" },
      fields: ["address_components", "geometry", "icon", "name"],
      strictBounds: false,
    };

    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current as HTMLInputElement,
      options
    );
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      console.log({ place });
      setMarker(place);
      // console.log(inputRef.current?.value)
      setAddress(inputRef.current?.value as string);
      const address: {
        [key: string]: any;
      } = {};
      if (place.address_components) {
        for (let index = 0; index < place.address_components.length; index++) {
          const element = place.address_components[index];
          address[element.types[0]] = element.long_name;
        }
      }
      getDistanceRef.current({
        lat: place.geometry?.location?.lat() as number,
        lng: place.geometry?.location?.lng() as number,
      });
      setLocation({
        lat: place.geometry?.location?.lat(),
        lng: place.geometry?.location?.lng(),
        address,
      });
    });
  }, [isLoaded]);
  useEffect(() => {
    if (address !== "" && inputRef.current) {
      inputRef.current.value = address;
      // geocode({ address: address })
    };
  }, [address]);
  return (
    <>
      <input
        ref={inputRef}
        id="pac-input"
        type="text"
        placeholder="Busque su dirección"
        className="input-custom"
        autoComplete="off"
      />

      {isLoaded ? (
        <>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {markers.map((marker, i) => (
              <Marker position={marker} key={i} draggable={true}
              onDragEnd={onDragMarker}
              />
            ))}
          </GoogleMap>
        </>
      ) : (
        <></>
      )}
      <h5>Distancia aproximada: {(distance / 1000).toFixed(2)} km</h5>
      {time && <h5>Tiempo aproximado: {time}</h5>}
      <h3 style={{ textAlign: "center" }}>
        Costo de Envio: $ {shippingPrice.toFixed(2)}
      </h3>
      <h3 style={{ textAlign: "center" }}>
        Total de Pedido: $ {(shippingPrice + orderTotal).toFixed(2)}
      </h3>
    </>
  );
};
